import axios from "axios";
import config from "../config";
import { TRIAL_EXPIRED_EVENT } from "../utils/trialExpiry";

import { EventSourcePolyfill } from "event-source-polyfill";

const { apiEndpoint } = config;

interface QuerySchema {
  [key: string]: string;
}

export interface HttpServiceSchema {
  httpGet: (path: string, query: QuerySchema) => Promise<any>;
  httpGetBlob: (path: string, query: QuerySchema) => Promise<any>;
  httpDelete: (path: string, query: QuerySchema, payload: any) => Promise<any>;
  httpPost: (path: string, query: QuerySchema, payload: any) => Promise<any>;
  httpPut: (path: string, query: QuerySchema, payload: any) => Promise<any>;
}

/**
 * This function returns an encoded URL using the path and query supplied in the arguments
 * @param {string} path the path to microservice endpoint
 * @param {QuerySchema} query optional query containing params in key value pairs
 * @returns {object} encodedURL, headers
 */
interface GetEncodedURLReturnSchema {
  url: string;
  headers: {
    "x-is-loginradius-ajax": boolean;
    [key: string]: any;
  };
}

export async function getHeaders(mailazyHeader: any): Promise<GetEncodedURLReturnSchema["headers"]> {
  const headers: GetEncodedURLReturnSchema["headers"] = {
    "x-is-loginradius-ajax": true,
    "Content-Type": "application/json",
    ...mailazyHeader
  };

  return headers;
}

async function getEncodedURL(path: string, query: QuerySchema): Promise<GetEncodedURLReturnSchema> {
  const encodedURL = `${apiEndpoint}${path}`;

  let urlEncodedQuery = "?";
  const mailazyHeader = query["MailazyHeader"];
  delete query["MailazyHeader"];
  for (const key in query) {
    urlEncodedQuery += key + "=" + encodeURIComponent(query[key]) + "&";
  }

  const headers = await getHeaders(mailazyHeader);

  const url = encodedURL + urlEncodedQuery;
  return { url, headers };
}

const handleErrorResp = (err: any) => {
  if (err.response?.status === 401) {
    if (location.pathname !== "/logout" && location.pathname !== "/login") {
      location.href = "/logout";
    }
    return Promise.reject(err.response.data);
  }
  if (err.response?.status === 403 && err.response?.data?.Reason === "TRIAL_EXPIRED") {
    window.dispatchEvent(new Event(TRIAL_EXPIRED_EVENT));
    return Promise.reject(err.response.data);
  }
  return err.response?.data ? Promise.reject(err.response.data) : Promise.reject(err);
};

/**
 * This function makes a HTTP GET request using the args provided
 * @param {string} path the path to the microservice endpoint
 * @param {QuerySchema} query optional query containing params in keyvalue pairs
 * @returns {Promise} promise containing response data or errors
 */
export const httpGet = async (path: string, query = {}, withCredentials = true) => {
  const { url, headers } = await getEncodedURL(path, query);
  return axios
    .get(url, { headers, withCredentials })
    .then((response) => response.data)
    .catch((err) => handleErrorResp(err));
};

/**
 * This function makes a HTTP GET request for a blob (e.g., file download) using the args provided
 * @param {string} path the path to the microservice endpoint
 * @param {QuerySchema} query optional query containing params in keyvalue pairs
 * @returns {Promise} promise containing response data or errors
 */
export const httpGetBlob = async (path: string, query = {}) => {
  const { url, headers } = await getEncodedURL(path, query);
  return axios
    .get(url, { headers, responseType: "blob", withCredentials: true })
    .then((response) => response)
    .catch((err) => handleErrorResp(err));
};

/**
 * This function makes a HTTP POST request for a blob (e.g., file download) using the args provided
 * @param {string} path the path to the microservice endpoint
 * @param {QuerySchema} query optional query containing params in keyvalue pairs
 * @param {QuerySchema} payload optional body containing the request payload
 * @returns {Promise} promise containing response data or errors
 */
export const httpPostBlob = async (path: string, query = {}, payload = {}) => {
  const { url, headers } = await getEncodedURL(path, query);
  return axios
    .post(url, payload, { headers, responseType: "blob", withCredentials: true })
    .then((response) => response)
    .catch((err) => handleErrorResp(err));
};

/**
 * This function makes a HTTP POST request using the args provided
 * @param {string} path the path to the microservice endpoint
 * @param {QuerySchema} query optional query containing params in keyvalue pairs
 * @param {QuerySchema} payload optional body containing the request payload
 * @returns {Promise} promise containing response data or errors
 */
export const httpPost = async (path: string, query = {}, payload = {}, extraHeaders = {}, withCredentials = true) => {
  const { url, headers } = await getEncodedURL(path, query);
  return axios
    .post(url, payload, { headers: { ...headers, ...extraHeaders }, withCredentials: withCredentials })
    .then((response) => response.data)
    .catch((err) => handleErrorResp(err));
};

/**
 * This function makes a HTTP PUT request using the args provided
 * @param path the path to the microservice endpoint
 * @param query optional query containing params in keyvalue pairs
 * @param payload optional body containing the request payload
 * @returns promise containing response data or errors
 */
export const httpPut = async (path: string, query = {}, payload = {}) => {
  const { url, headers } = await getEncodedURL(path, query);
  return axios
    .put(url, payload, { headers, withCredentials: true })
    .then((response) => response.data)
    .catch((err) => handleErrorResp(err));
};

/**
 * This function makes a HTTP DELETE request using the args provided
 * @param {string} path the path to the microservice endpoint
 * @param {QuerySchema} query optional query containing params in keyvalue pairs
 * @param {QuerySchema} payload optional payload containing data in keyvalue pairs;
 *   although not customary, some of our endpoints for DELETE requests expect a payload.
 * @returns {Promise} containing response data or errors
 */
export const httpDelete = async (path: string, query = {}, payload = {}) => {
  const { url, headers } = await getEncodedURL(path, query);
  return axios
    .delete(url, { data: payload, headers, withCredentials: true })
    .then((response) => response.data)
    .catch((err) => handleErrorResp(err));
};

/**
 * This function makes a Event Source request using the args provided
 * @param {string} path the path to the microservice endpoint
 * @param {QuerySchema} query optional query containing params in keyvalue pairs
 * @returns {Promise} promise containing response data or errors
 */
export const eventSource = async (path: string, query = {}, headers = {}) => {
  const { url } = await getEncodedURL(path, query);
  return new EventSourcePolyfill(url, {
    headers: headers,
    heartbeatTimeout: 600000 // Set timeout to 10 minutes (600,000 milliseconds)
  });
};
